import { z } from 'zod';

import { SubscriptionDTO } from '../me';
import { IsoDate, Locale, Page, PageQuery, Platform, Role, Uid } from '../primitives';
import { SessionDTO } from '../sessions';
import { TextFilter, Timestamps } from './common';
import { GrantRedemptionDTO } from './grants';

/**
 * Phase-1 access status (docs/07 §1: all-or-nothing). `until` is the furthest
 * `current_period_end` of the user's live subscriptions, `null` when one of them is perpetual.
 */
export const CmsUserAccess = z.object({
  hasAccess: z.boolean(),
  until: IsoDate.nullable(),
});
export type CmsUserAccess = z.infer<typeof CmsUserAccess>;

export const CmsUserDTO = z
  .object({
    id: Uid,
    email: z.string().nullable(),
    emailVerified: z.boolean(),
    displayName: z.string().nullable(),
    photoUrl: z.string().nullable(),
    isAnonymous: z.boolean(),
    role: Role,
    locale: Locale,
    stripeCustomerId: z.string().nullable(),
    blocked: z.boolean(),
    lastLoginAt: IsoDate.nullable(),
    deletedAt: IsoDate.nullable(),
    /** "Activo hasta …" / "Sin acceso" of CMS-10. */
    access: CmsUserAccess,
  })
  .merge(Timestamps);
export type CmsUserDTO = z.infer<typeof CmsUserDTO>;

/** `GET /cms/users?q=` (admin) */
export const CmsUsersQuery = PageQuery.extend({
  q: TextFilter.optional(),
  role: Role.optional(),
  /** `yes` → only users with a live subscription, `no` → only users without one. */
  access: z.enum(['yes', 'no']).optional(),
  blocked: z.enum(['yes', 'no']).optional(),
});
export type CmsUsersQuery = z.infer<typeof CmsUsersQuery>;
export type CmsUsersQueryInput = z.input<typeof CmsUsersQuery>;

export const CmsUsersResponse = Page(CmsUserDTO);
export type CmsUsersResponse = z.infer<typeof CmsUsersResponse>;

export const CmsUserParams = z.object({ id: Uid });
export type CmsUserParams = z.infer<typeof CmsUserParams>;

/** `PATCH /cms/users/:id` (admin): role and block. */
export const CmsUserPatchBody = z
  .object({
    role: Role,
    blocked: z.boolean(),
  })
  .partial()
  .refine((body) => Object.keys(body).length > 0, { message: 'At least one field is required' });
export type CmsUserPatchBody = z.infer<typeof CmsUserPatchBody>;

export const CmsUserResponse = z.object({ user: CmsUserDTO });
export type CmsUserResponse = z.infer<typeof CmsUserResponse>;

/** A device this user has hosted a session from (CMS-10 "Dispositivos"). */
export const CmsUserDeviceDTO = z.object({
  id: z.string(),
  platform: Platform,
  model: z.string().nullable(),
  lastSeenAt: IsoDate,
  blocked: z.boolean(),
  /** Sessions this user hosted from the device. */
  sessionCount: z.number().int().nonnegative(),
});
export type CmsUserDeviceDTO = z.infer<typeof CmsUserDeviceDTO>;

/** A session the user hosted or joined (CMS-10 "Sesiones · host N · invitado M"). */
export const CmsUserSessionDTO = SessionDTO.extend({
  role: z.enum(['host', 'guest']),
  songsPlayed: z.number().int().nonnegative(),
});
export type CmsUserSessionDTO = z.infer<typeof CmsUserSessionDTO>;

/** `GET /cms/users/:id` */
export const CmsUserDetailResponse = z.object({
  user: CmsUserDTO,
  subscriptions: z.array(SubscriptionDTO),
  sessions: z.array(CmsUserSessionDTO),
  redemptions: z.array(GrantRedemptionDTO),
  devices: z.array(CmsUserDeviceDTO),
});
export type CmsUserDetailResponse = z.infer<typeof CmsUserDetailResponse>;
